/** @format */
import { format } from 'date-fns/format';
import { de, enGB, enIE, enUS, es, fr, it, nl, pt } from 'date-fns/locale';
import { parse } from 'date-fns/parse';
import { parseISO } from 'date-fns/parseISO';

import { MarketCode, ukMarket } from '@toucan/config/markets';

export const getFormatForMarket = (market: MarketCode): string => {
    switch (market) {
        case MarketCode.EN_US:
            return 'MM/dd/yyyy';
        case MarketCode.DE_DE:
            return 'dd.MM.yyyy';
        case MarketCode.EN_UK:
        case MarketCode.FR_FR:
        default:
            return 'dd/MM/yyyy';
    }
};

export const getDateFnsLocale = (market?: MarketCode) => {
    switch (market) {
        case MarketCode.NL_NL:
        case MarketCode.NL_BE:
            return nl;
        case MarketCode.DE_DE:
        case MarketCode.DE_AT:
            return de;
        case MarketCode.FR_FR:
        case MarketCode.FR_BE:
            return fr;
        case MarketCode.EN_EU:
        case MarketCode.EN_IE:
            return enIE;
        case MarketCode.EN_US:
            return enUS;
        case MarketCode.IT_IT:
            return it;
        case MarketCode.ES_ES:
            return es;
        case MarketCode.PT_PT:
            return pt;
        default:
            return enGB;
    }
};

export const getFormattedDate = (
    marketCode: MarketCode,
    date: string | undefined,
    options: {
        sourceFormat?: string;
        targetFormat?: string;
    } = {}
): string => {
    if (!date) {
        return 'Invalid Date';
    }

    try {
        const locale = getDateFnsLocale(marketCode);
        const formatString = options?.targetFormat
            ? options?.targetFormat
            : getFormatForMarket(marketCode);
        const parsedDate = options?.sourceFormat
            ? parse(date, options?.sourceFormat, new Date())
            : parseISO(date);
        return format(parsedDate, formatString, { locale });
    } catch (e) {
        // noop
    }

    return 'Invalid Date';
};

/**
 * en_EU is not a valid locale, so defaults to en_GB
 */
export const getValidIsoForMarketIso = (marketIso: string) => {
    switch (marketIso) {
        case 'en_EU':
            return ukMarket.iso;

        default:
            return marketIso;
    }
};

/**
 * uses Intl built in DateTimeFormat to get a localised date time format
 */
export const getFormattedDateTime = (
    iso: string,
    date?: string | Date,
    dateTimeOptions?: Intl.DateTimeFormatOptions
) => {
    if (!date) {
        return 'Invalid Date';
    }

    try {
        return new Intl.DateTimeFormat(
            getValidIsoForMarketIso(iso).replace('_', '-'),
            dateTimeOptions
        ).format(new Date(date));
    } catch (e) {
        // noop
    }
    return 'Invalid Date';
};
